import { useBi } from '@wix/yoshi-flow-editor';
import {
  googleAdsensePageOpen, // 400
  adsClickOnButton, // 403
  googleAdsenseAdView, // 870
} from '@wix/bi-logger-labs-data/v2';

const PAGE_NAMES = {
  OLD_USERS: 'old_users_page',
  DISCONNECTED: 'manage_account_not_connected',
  IN_REVIEW: 'manage_waiting_for_approval',
  SUPPORT: 'support_tab',
  ADUNITS_EMPTY_LIST: 'manage_account_connected_ad_not_created',
  AD_UNIT_NOT_CONNECTED: 'manage_account_connected_ad_not_connected',
  AD_UNIT_CONNECTED: 'manage_account_connected_ad_connected',
  CREATE_FIRST_ADUNIT: 'ads_tab_no_ads_created',
  ADUNIT_NOT_CONNECTED: 'ads_tab_no_ads_connected',
  ADUNIT_CONNECTED_FIXED: 'ads_tab_fixed_ad',
  ADUNIT_CONNECTED_RESPONSIVE: 'ads_tab_responsive_ad',
  LIVE_SITE: 'live_site',
} as const;

type PageName = (typeof PAGE_NAMES)[keyof typeof PAGE_NAMES];
type ButtonDescriptions = Record<string, string>;

type BiMethods<T extends ButtonDescriptions> = {
  pageOpen: () => void;
} & {
  [K in keyof T]: () => void;
};

const createBiMethods =
  (bi: ReturnType<typeof useBi>) =>
  <T extends ButtonDescriptions>(
    pageName: PageName,
    buttonDescriptions: T,
  ): BiMethods<T> =>
    ({
      pageOpen: (): void => {
        bi.report(googleAdsensePageOpen({ page_name: pageName }));
      },
      ...Object.fromEntries(
        Object.entries(buttonDescriptions).map(([key, description]) => [
          key,
          (): void => {
            bi.report(adsClickOnButton({ origin: pageName, description }));
          },
        ]),
      ),
    } as BiMethods<T>);

export const useBiLogger = () => {
  const bi = useBi();

  const createMethods = createBiMethods(bi);
  return {
    biOldUsers: createMethods(PAGE_NAMES.OLD_USERS, {
      buttonClick: 'connect_account',
    }),
    biDisconnected: createMethods(PAGE_NAMES.DISCONNECTED, {
      buttonClick: 'go_to_dashboard',
    }),
    biInReview: createMethods(PAGE_NAMES.IN_REVIEW, {
      buttonClick: 'go_to_dashboard',
    }),
    biSupport: createMethods(PAGE_NAMES.SUPPORT, {
      seeAllArticlesClick: 'see_all_articles',
      wixAssistantClick: 'wix_assistant',
      adsenseHelpClick: 'adsense_help',
      contactCustomerCareClick: 'contact_customer_care',
    }),
    biAdunitsEmptyList: createMethods(PAGE_NAMES.ADUNITS_EMPTY_LIST, {
      createAdUnitClick: 'create_ad_unit',
      manageAdUnitClick: 'manage_ads',
    }),
    biAdUnitNotConnected: createMethods(PAGE_NAMES.AD_UNIT_NOT_CONNECTED, {
      connectAdUnitClick: 'connect_ad_unit',
      manageAdUnitClick: 'manage_ads',
    }),

    biAdUnitConnected: createMethods(PAGE_NAMES.AD_UNIT_CONNECTED, {
      duplicateAdUnitClick: 'duplicate_ad',
      manageAdUnitClick: 'manage_ads',
    }),

    biCreateFirstAdunit: createMethods(PAGE_NAMES.CREATE_FIRST_ADUNIT, {
      newAdUnitClick: 'new_ad_unit',
    }),

    biAdunitNotConnected: createMethods(PAGE_NAMES.ADUNIT_NOT_CONNECTED, {
      newAdUnitClick: 'new_ad_unit',
    }),

    biAdunitConnectedFixed: createMethods(PAGE_NAMES.ADUNIT_CONNECTED_FIXED, {
      newAdUnitClick: 'new_ad_unit',
    }),

    biAdunitConnectedResponsive: createMethods(
      PAGE_NAMES.ADUNIT_CONNECTED_RESPONSIVE,
      {
        newAdUnitClick: 'new_ad_unit',
      },
    ),
    biLiveSite: {
      adView: () => {
        bi.report(googleAdsenseAdView({}));
      },
      adClick: () => {
        bi.report(
          adsClickOnButton({
            origin: PAGE_NAMES.LIVE_SITE,
            description: 'live_site_ad_click',
          }),
        );
      },
    },
  };
};
